import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  updateDoc,
  writeBatch,
  runTransaction,
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { Inventory, InventoryJournal } from '../types/inventory';

export interface InventoryTransactionParams {
  transactionType: 'zoho_pull' | 'loadout' | 'settlement' | 'sales' | 'production_inward';
  fromWarehouseId: string; // "NA" for external sources
  toWarehouseId: string;   // "NA" for external destinations  
  productId: string;
  productName: string;
  unit: string;
  quantity: number;
  rate?: number;
  reference: string;
  notes?: string;
  createdBy: string;
}

export interface BatchInventoryTransactionParams {
  transactions: InventoryTransactionParams[];
  batchReference: string;
}

export interface InventoryTransactionResult {
  success: boolean;
  journalId?: string;
  error?: string;
}

export interface BatchInventoryTransactionResult {
  success: boolean;
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  journalIds: string[];
  errors: string[];
}

class UnifiedInventoryTransactionService {

  /**
   * Process a single inventory transaction with atomic operations
   */
  static async processInventoryTransaction(params: InventoryTransactionParams): Promise<InventoryTransactionResult> {
    try {
      // First, query for existing inventory records outside the transaction
      let sourceInventoryRef: any = null;
      let sourceData: Inventory | null = null;
      let destinationInventoryRef: any = null;
      let destinationData: Inventory | null = null;
      // Precompute destination type and display name (avoid reads inside transaction)
      const destinationIsInternal = params.toWarehouseId !== "NA" && !this.isOutletWarehouse(params.toWarehouseId);
      const precomputedDestType = destinationIsInternal ? this.classifyWarehouseType(params.toWarehouseId) : undefined;
      const precomputedDestName = destinationIsInternal && precomputedDestType
        ? await this.resolveWarehouseName(params.toWarehouseId, precomputedDestType)
        : undefined;

      if (params.fromWarehouseId !== "NA") {
        const sourceInventoryQuery = query(
          collection(db, 'inventory'),
          where('warehouseId', '==', params.fromWarehouseId),
          where('productId', '==', params.productId)
        );
        const sourceInventorySnapshot = await getDocs(sourceInventoryQuery);
        
        if (!sourceInventorySnapshot.empty) {
          sourceInventoryRef = sourceInventorySnapshot.docs[0].ref;
          sourceData = sourceInventorySnapshot.docs[0].data() as Inventory;
        } else {
          throw new Error(`Source inventory not found: ${params.fromWarehouseId}_${params.productId}`);
        }
      }

      if (params.toWarehouseId !== "NA") {
        const destinationInventoryQuery = query(
          collection(db, 'inventory'),
          where('warehouseId', '==', params.toWarehouseId),
          where('productId', '==', params.productId)
        );
        const destinationInventorySnapshot = await getDocs(destinationInventoryQuery);
        
        if (!destinationInventorySnapshot.empty) {
          destinationInventoryRef = destinationInventorySnapshot.docs[0].ref;
          destinationData = destinationInventorySnapshot.docs[0].data() as Inventory;
        }
      }

      // Now run the transaction with the refs we found
      return await runTransaction(db, async (transaction) => {
        const timestamp = serverTimestamp();
        const today = new Date().toISOString().split('T')[0];
        
        // 1. Create journal entry first (atomic principle)
        const journalRef = doc(collection(db, 'inventory_journal'));
        const journalId = journalRef.id;
        
        let sourceStockBefore = 0;
        let sourceStockAfter = 0;
        let destinationStockBefore = 0;
        let destinationStockAfter = 0;

        // 2. Update source inventory (if not external)
        if (params.fromWarehouseId !== "NA" && sourceInventoryRef && sourceData) {
          sourceStockBefore = sourceData.closingStock;
          sourceStockAfter = Math.max(0, sourceStockBefore - params.quantity);
          
          transaction.update(sourceInventoryRef, {
            outwardStock: (sourceData.outwardStock || 0) + params.quantity,
            closingStock: sourceStockAfter,
            inventoryJournalId: journalId,
            lastUpdated: timestamp,
            updatedAt: timestamp,
            updatedBy: params.createdBy
          });
        }

        // 3. Update destination inventory (if not external and not an outlet)
        if (params.toWarehouseId !== "NA" && !this.isOutletWarehouse(params.toWarehouseId)) {
          if (destinationInventoryRef && destinationData) {
            // Update existing destination inventory
            destinationStockBefore = destinationData.closingStock;
            destinationStockAfter = destinationStockBefore + params.quantity;
            
            transaction.update(destinationInventoryRef, {
              inwardStock: (destinationData.inwardStock || 0) + params.quantity,
              closingStock: destinationStockAfter,
              inventoryJournalId: journalId,
              lastUpdated: timestamp,
              updatedAt: timestamp,
              updatedBy: params.createdBy,
              // Correct any prior misclassification for routes
              warehouseType: precomputedDestType ?? destinationData.warehouseType,
              warehouseName: precomputedDestName ?? destinationData.warehouseName
            });
          } else {
            // Create new inventory record for destination
            const newInventoryRef = doc(collection(db, 'inventory'));
            const newInventoryRecord: Inventory = {
              id: newInventoryRef.id,
              warehouseId: params.toWarehouseId,
              warehouseName: precomputedDestName || `Warehouse ${params.toWarehouseId}`,
              warehouseType: precomputedDestType || 'main',
              date: today,
              productId: params.productId,
              productName: params.productName,
              unit: params.unit,
              openingStock: 0,
              inwardStock: params.quantity,
              outwardStock: 0,
              closingStock: params.quantity,
              inventoryJournalId: journalId,
              lastUpdated: timestamp,
              // ERP-neutral flag for future integrations
              syncedWithERP: false,
              createdAt: timestamp,
              updatedAt: timestamp,
              createdBy: params.createdBy,
              updatedBy: params.createdBy
            };
            
            destinationStockBefore = 0;
            destinationStockAfter = params.quantity;
            
            transaction.set(newInventoryRef, newInventoryRecord);
          }
        }

        // 4. Create simplified journal entry
        // 🔍 DEBUG: Track what's being set in journal
        console.log(`🔍 DEBUG processInventoryTransaction - params.toWarehouseId: "${params.toWarehouseId}"`);
        console.log(`🔍 DEBUG processInventoryTransaction - params.toWarehouseId === "NA": ${params.toWarehouseId === "NA"}`);
        
        const journalEntry: InventoryJournal = {
          journalId: journalId,
          fromWarehouseId: params.fromWarehouseId === "NA" ? "" : params.fromWarehouseId,
          toWarehouseId: params.toWarehouseId === "NA" ? "" : params.toWarehouseId,
          beforeQty: params.fromWarehouseId === "NA" ? 0 : sourceStockBefore,
          qty: params.quantity,
          afterQty: params.fromWarehouseId === "NA" ? 0 : sourceStockAfter,
          productId: params.productId
        };

        // 🔍 DEBUG: Check final journal entry toWarehouseId
        console.log(`🔍 DEBUG processInventoryTransaction - final journalEntry.toWarehouseId: "${journalEntry.toWarehouseId}"`);

        transaction.set(journalRef, journalEntry);

        return { success: true, journalId };
      });
    } catch (error) {
      console.error('Inventory transaction failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Process multiple inventory transactions in a batch
   */
  static async processBatchInventoryTransactions(params: BatchInventoryTransactionParams): Promise<BatchInventoryTransactionResult> {
    const results: BatchInventoryTransactionResult = {
      success: false,
      totalTransactions: params.transactions.length,
      successfulTransactions: 0,
      failedTransactions: 0,
      journalIds: [],
      errors: []
    };

    for (const transaction of params.transactions) {
      const result = await this.processInventoryTransaction(transaction);
      
      if (result.success) {
        results.successfulTransactions++;
        if (result.journalId) {
          results.journalIds.push(result.journalId);
        }
      } else {
        results.failedTransactions++;
        if (result.error) {
          results.errors.push(result.error);
        }
      }
    }

    results.success = results.failedTransactions === 0;
    
    console.log(`Batch inventory transaction completed: ${results.successfulTransactions}/${results.totalTransactions} successful`);
    return results;
  }


  /**
   * Process loadout transaction
   */
  static async processLoadoutTransaction(
    fromWarehouseId: string,
    toVehicleId: string,
    allocations: Array<{
      productId: string;
      productName: string;
      unit: string;
      quantity: number;
      rate: number;
    }>,
    beatId: string,
    createdBy: string
  ): Promise<BatchInventoryTransactionResult> {
    const transactions: InventoryTransactionParams[] = allocations.map(allocation => ({
      transactionType: 'loadout',
      fromWarehouseId,
      toWarehouseId: toVehicleId,
      productId: allocation.productId,
      productName: allocation.productName,
      unit: allocation.unit,
      quantity: allocation.quantity,
      rate: allocation.rate,
      reference: beatId,
      notes: `Loadout for beat ${beatId}`,
      createdBy
    }));

    const batchParams: BatchInventoryTransactionParams = {
      transactions,
      batchReference: `LOADOUT-${beatId}`
    };

    return await this.processBatchInventoryTransactions(batchParams);
  }

  /**
   * Process sales transaction
   */
  static async processSalesTransaction(
    fromVehicleId: string,
    saleItems: Array<{
      productId: string;
      productName: string;
      unit: string;
      quantity: number;
      rate: number;
    }>,
    outletId: string, // 🔧 FIX: Add outletId parameter for journal toWarehouseId
    saleId: string,
    createdBy: string
  ): Promise<BatchInventoryTransactionResult> {
    const transactions: InventoryTransactionParams[] = saleItems.map(item => ({
      transactionType: 'sales',
      fromWarehouseId: fromVehicleId,
      toWarehouseId: outletId, // 🔧 FIX: Use outletId instead of "NA"
      productId: item.productId,
      productName: item.productName,
      unit: item.unit,
      quantity: item.quantity,
      rate: item.rate,
      reference: saleId,
      notes: `Sale transaction ${saleId}`,
      createdBy
    }));

    const batchParams: BatchInventoryTransactionParams = {
      transactions,
      batchReference: `SALE-${saleId}`
    };

    return await this.processBatchInventoryTransactions(batchParams);
  }

  /**
   * Process settlement transaction (returns)
   */
  static async processSettlementTransaction(
    fromVehicleId: string,
    toWarehouseId: string,
    returnItems: Array<{
      productId: string;
      productName: string;
      unit: string;
      quantity: number;
    }>,
    settlementId: string,
    createdBy: string
  ): Promise<BatchInventoryTransactionResult> {
    const transactions: InventoryTransactionParams[] = returnItems.map(item => ({
      transactionType: 'settlement',
      fromWarehouseId: fromVehicleId,
      toWarehouseId,
      productId: item.productId,
      productName: item.productName,
      unit: item.unit,
      quantity: item.quantity,
      reference: settlementId,
      notes: `Settlement return ${settlementId}`,
      createdBy
    }));

    const batchParams: BatchInventoryTransactionParams = {
      transactions,
      batchReference: `SETTLEMENT-${settlementId}`  
    };

    return await this.processBatchInventoryTransactions(batchParams);
  }

  /**
   * Map transaction type to journal transaction type
   */
  private static mapTransactionType(transactionType: 'zoho_pull' | 'loadout' | 'settlement' | 'sales'): 'opening_stock' | 'warehouse_to_route' | 'return' | 'sale' {
    switch (transactionType) {
      case 'zoho_pull':
        return 'opening_stock';
      case 'loadout':
        // Route-first: loadouts are main → route movements
        return 'warehouse_to_route';
      case 'settlement':
        return 'return';
      case 'sales':
        return 'sale';
      default:
        return 'opening_stock';
    }
  }

  /**
   * Get inventory transaction history for a product/warehouse
   */
  static async getInventoryTransactionHistory(
    warehouseId: string,
    productId: string,
    startDate?: string,
    endDate?: string
  ): Promise<InventoryJournal[]> {
    // Implementation for querying transaction history
    // This would use Firestore queries to get journal entries
    return [];
  }

  /**
   * Get current inventory balance for a product/warehouse
   */
  static async getCurrentInventoryBalance(
    warehouseId: string,
    productId: string
  ): Promise<Inventory | null> {
    // Query for inventory using warehouseId and productId fields (no date)
    const inventoryQuery = query(
      collection(db, 'inventory'),
      where('warehouseId', '==', warehouseId),
      where('productId', '==', productId)
    );
    const inventorySnapshot = await getDocs(inventoryQuery);
    
    return !inventorySnapshot.empty 
      ? { id: inventorySnapshot.docs[0].id, ...inventorySnapshot.docs[0].data() } as Inventory 
      : null;
  }

  /**
   * Check if a warehouse ID represents an outlet (vs warehouse/vehicle)
   */
  private static isOutletWarehouse(warehouseId: string): boolean {
    // Outlets are typically numeric IDs (like "100034")
    // Warehouses have patterns like "MUM-WH02" 
    // Vehicles have patterns like "VH-001" or "VEH-002"
    // Stock Gain Loss WH is "STOCK-GAIN-LOSS-WH"
    
    if (warehouseId === "STOCK-GAIN-LOSS-WH") {
      return false;
    }
    
    // If it's purely numeric, it's likely an outlet
    return /^\d+$/.test(warehouseId);
  }

  /**
   * Validate inventory transaction before processing
   */
  static async validateInventoryTransaction(params: InventoryTransactionParams): Promise<{ valid: boolean; error?: string }> {
    // Check if source has sufficient stock (for outward transactions)
    if (params.fromWarehouseId !== "NA") {
      const currentBalance = await this.getCurrentInventoryBalance(params.fromWarehouseId, params.productId);
      if (!currentBalance) {
        return { valid: false, error: `No inventory found for product ${params.productId} in warehouse ${params.fromWarehouseId}` };
      }
      
      if (currentBalance.closingStock < params.quantity) {
        return { valid: false, error: `Insufficient stock. Available: ${currentBalance.closingStock}, Required: ${params.quantity}` };
      }
    }

    return { valid: true };
  }

  /** Heuristic classification for warehouse id → type */
  private static classifyWarehouseType(id: string): 'main' | 'route' {
    if (!id) return 'main';
    const upper = id.toUpperCase();
    // Treat vehicle-like IDs as route inventory holders in the new model
    if (upper.startsWith('VEH-') || upper.startsWith('VH-') || upper.startsWith('V-')) return 'route';
    if (upper.startsWith('RT') || upper.startsWith('ROUTE-')) return 'route';
    if (upper === 'STOCK-GAIN-LOSS-WH') return 'main';
    // Warehouses tend to have patterns like MUM-, WH-, etc.
    return 'main';
  }

  /** Resolve display name for a warehouse/route id (route-first model) */
  private static async resolveWarehouseName(id: string, type: 'main' | 'route'): Promise<string> {
    try {
      if (type === 'route') {
        // Try to find route by custom routeId field
        const routesSnap = await getDocs(query(collection(db, 'routes'), where('routeId', '==', id)));
        if (!routesSnap.empty) {
          const route = routesSnap.docs[0].data() as any;
          return route.name || route.routeName || `Route ${id}`;
        }
        return `Route ${id}`;
      }
      // Main warehouse: try to resolve by warehouseId custom field
      const whSnap = await getDocs(query(collection(db, 'warehouses'), where('warehouseId', '==', id)));
      if (!whSnap.empty) {
        const wh = whSnap.docs[0].data() as any;
        return wh.name || `Warehouse ${id}`;
      }
      return `Warehouse ${id}`;
    } catch (e) {
      return type === 'route' ? `Route ${id}` : `Warehouse ${id}`;
    }
  }
}

export { UnifiedInventoryTransactionService };
